/**
 * Conversation focus — presentation-layer continuity after a Sales Brain turn.
 *
 * Stores last user + agent snippet (sessionStorage). Used only to:
 *  - rank EXISTING page/section embedding pills (section inventory first)
 *  - flavor bar lead when the current page can continue the thread
 *  - optional voice continuity (caller gates: voice session only)
 *
 * Does NOT invent questions or answers. Does NOT call the LLM.
 * Answer generation stays Sales Brain (or future cache) on engage only.
 */

import {
  canonicalQuestionKey,
  getAskedPageQuestions,
  normalizeQuestion,
} from "./page-pills.ts";

const STORAGE_KEY = "bhai_conversation_focus_v1";

/** Default floor for focusMatchesInventory — weak one-token hits should not flip bar continuity. */
export const FOCUS_MATCH_FLOOR = 0.3;

export type ConversationFocus = {
  /** Last visitor question (full-ish). */
  lastUser: string;
  /** Short agent preview for scoring / copy (not full answer dump). */
  lastAgentPreview: string;
  /** Short topic phrase for bar lead (derived from the visitor's question). */
  topicLabel: string;
  /**
   * Next-ask strings from runtime context_next_ask chips only (engine inventory).
   * Never invent product/page names here — fleet-wide.
   */
  nextAskHints: string[];
  /** epoch ms */
  at: number;
};

const STOP = new Set([
  "the", "a", "an", "and", "or", "to", "of", "in", "on", "for", "is", "are",
  "it", "that", "this", "with", "you", "your", "we", "our", "so", "then",
  "than", "as", "at", "by", "from", "be", "can", "will", "do", "does", "did",
  "what", "how", "why", "when", "where", "who", "which", "about", "into",
  "just", "not", "but", "if", "was", "were", "have", "has", "had", "them",
  "they", "their", "also", "more", "most", "only", "than", "very", "really",
]);

function tokenize(s: string): Set<string> {
  const out = new Set<string>();
  for (const w of s.toLowerCase().match(/[a-z0-9$%]{3,}/g) ?? []) {
    if (!STOP.has(w)) out.add(w);
  }
  return out;
}

function truncateTopicLabel(s: string, max = 42): string {
  let t = s.replace(/\s+/g, " ").trim();
  // Drop trailing commas / partial list crumbs from mid-cut.
  t = t.replace(/[,;:]+$/g, "").trim();
  if (t.length <= max) return t;
  t = t.slice(0, max).replace(/\s+\S*$/, "").trim();
  t = t.replace(/[,;:]+$/g, "").trim();
  return t || s.slice(0, max).trim();
}

/** upload → uploading, use → using, add → adding (fleet-wide, not product-specific). */
function verbToGerund(verb: string): string {
  const v = verb.toLowerCase().trim();
  if (!v) return "about";
  if (v.endsWith("ie")) return `${v.slice(0, -2)}ying`;
  if (v.endsWith("e") && !v.endsWith("ee") && v.length > 2) {
    return `${v.slice(0, -1)}ing`;
  }
  return `${v}ing`;
}

/**
 * Shorten a list-ish object phrase for speech:
 * "pricing guidance, proof points, policies, or product catalogs"
 * → "pricing guidance and proof points"
 */
function summarizeListObject(obj: string, maxItems = 2): string {
  let t = obj.replace(/\s+/g, " ").trim();
  // Drop trailing "to {product/site}…" / "for {audience}…"
  t = t.replace(/\s+(?:to|for|with|on|in|from|into)\s+.+$/i, "").trim();
  t = t.replace(/[?!.]+$/g, "").trim();
  // Split on list commas / "or" / "and" without treating "and" inside first item only.
  const parts = t
    .split(/\s*,\s*|\s+or\s+|\s+and\s+/i)
    .map((p) => p.replace(/^[,;:\s]+|[,;:\s]+$/g, "").trim())
    .filter((p) => p.length >= 2);
  if (parts.length === 0) return truncateTopicLabel(t, 40);
  if (parts.length === 1) return truncateTopicLabel(parts[0]!, 40);
  const head = parts.slice(0, maxItems);
  if (head.length === 1) return truncateTopicLabel(head[0]!, 40);
  return truncateTopicLabel(`${head[0]} and ${head[1]}`, 48);
}

/** Labels that sound broken when spoken ("I upload…", mid-list crumbs). */
function isAwkwardTopicLabel(label: string): boolean {
  const t = label.replace(/\s+/g, " ").trim();
  if (t.length < 3) return true;
  if (/^(i|we|you|they|he|she|it)\s+/i.test(t)) return true;
  if (/,\s*$/.test(t) || /^(and|or|to|for)\b/i.test(t)) return true;
  // Incomplete question openers — not finished summaries like "how X works".
  if (/^(can|could|would|should)\b/i.test(t)) return true;
  if (/^(what|why|when|where|who)\b/i.test(t)) return true;
  if (/^how\s+(does|do|can|will|is|are)\b/i.test(t)) return true;
  if (/^(does|do|is|are)\s+/i.test(t)) return true;
  return false;
}

/**
 * Short natural topic for continuity lines ("Related to …").
 * Prefer a noun phrase or gerund summary — never a mangled question fragment
 * like "I upload pricing guidance, proof points,".
 * Fleet-wide patterns only (no product dictionary).
 */
export function topicLabelFromUserQuestion(user: string): string {
  const raw = user
    .replace(/\s+/g, " ")
    .trim()
    .replace(/[?!.]+$/g, "")
    .trim();
  if (!raw) return "that";

  // "How does/do/can X work" → "how X works"
  let m = raw.match(/^how\s+(?:does|do|can)\s+(.+?)\s+works?$/i);
  if (m?.[1]) {
    const label = truncateTopicLabel(`how ${m[1].trim()} works`);
    if (!isAwkwardTopicLabel(label)) return label;
  }

  // "How does/do/can X help|answer|convert|…" → "how X helps …" (natural phrase)
  m = raw.match(
    /^how\s+(?:does|do|can)\s+(.+?)\s+(help|helps|answer|convert|track|show|improve|capture|reveal)s?\s+(.+)$/i,
  );
  if (m?.[1] && m?.[2] && m?.[3]) {
    const subj = m[1].trim();
    const verb = m[2].toLowerCase().replace(/s$/, "") + "s"; // help → helps
    const rest = summarizeListObject(m[3].trim(), 3);
    const label = truncateTopicLabel(`how ${subj} ${verb} ${rest}`, 52);
    if (!isAwkwardTopicLabel(label) && label.split(/\s+/).length >= 3) {
      return label;
    }
  }

  // "What is/are X"
  m = raw.match(/^what\s+(?:is|are)\s+(.+)$/i);
  if (m?.[1]) {
    const label = truncateTopicLabel(summarizeListObject(m[1].trim(), 2), 42);
    if (!isAwkwardTopicLabel(label)) return label;
  }

  // "What does/do/can X show|…" → subject noun phrase
  m = raw.match(
    /^what\s+(?:does|do|can|could|will)\s+(.+?)\s+(?:typically\s+)?(?:show|help|track|mean|do|offer|include)\b/i,
  );
  if (m?.[1] && m[1].trim().length >= 3) {
    const label = truncateTopicLabel(m[1].trim(), 42);
    if (!isAwkwardTopicLabel(label)) return label;
  }

  // "Can/Could I/we/you upload|add|use|…" → "uploading pricing and proof points"
  // Fixes: "Can I upload A, B, or C to Product?" → not "I upload A,"
  m = raw.match(
    /^(?:can|could|may|should|will|would)\s+(?:i|we|you|one)\s+([a-z]+)\s+(.+)$/i,
  );
  if (m?.[1] && m?.[2]) {
    const gerund = verbToGerund(m[1]);
    const obj = summarizeListObject(m[2], 2);
    if (obj.length >= 3) {
      const label = truncateTopicLabel(`${gerund} ${obj}`, 48);
      if (!isAwkwardTopicLabel(label)) return label;
    }
  }

  // "Can/Could/Does X show|help|track|…" → subject noun phrase only
  m = raw.match(
    /^(?:can|could|does|do|will|would)\s+(.+?)\s+(?:show|help|track|handle|integrate|support|let|give|tell|answer|measure|prove|find|see|do|work with|work on|upload|run|connect)\b/i,
  );
  if (m?.[1] && m[1].trim().length >= 3) {
    // Avoid swallowing "I" as subject for "Can I upload…"
    if (!/^(i|we|you|one)$/i.test(m[1].trim())) {
      const label = truncateTopicLabel(m[1].trim(), 42);
      if (!isAwkwardTopicLabel(label)) return label;
    }
  }

  // "Tell me about X" / "Explain X"
  m = raw.match(/^(?:tell me (?:about |more about )?|explain |describe )(.+)$/i);
  if (m?.[1]) {
    const label = truncateTopicLabel(summarizeListObject(m[1], 2), 42);
    if (!isAwkwardTopicLabel(label)) return label;
  }

  // Light cleanup of leading question openers / fillers.
  let t = raw.replace(
    /^(can you |could you |would you |do you |does |is |are |what is |what are |how does |how do |how can |how |tell me (?:about |more about )?|explain |please |can i |could i |can we |could we |can |could )\s*/i,
    "",
  );
  t = t.replace(/^(about |regarding |re:? )/i, "").replace(/\s+/g, " ").trim();

  // First-person residual ("I upload X…") → gerund summary
  m = t.match(/^(?:i|we|you)\s+([a-z]+)\s+(.+)$/i);
  if (m?.[1] && m?.[2]) {
    const gerund = verbToGerund(m[1]);
    const obj = summarizeListObject(m[2], 2);
    if (obj.length >= 3) {
      const label = truncateTopicLabel(`${gerund} ${obj}`, 48);
      if (!isAwkwardTopicLabel(label)) return label;
    }
  }

  // Prefer first list/clause, not mid-comma cut of a long or-list.
  if (t.length > 40 || /,\s*/.test(t)) {
    t = summarizeListObject(t, 2);
  } else {
    t = truncateTopicLabel(t, 48);
  }

  if (isAwkwardTopicLabel(t)) return "that";
  return t || "that";
}

export function readConversationFocus(): ConversationFocus | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw) as ConversationFocus;
    if (!o || typeof o.lastUser !== "string" || !o.lastUser.trim()) return null;
    if (typeof o.at !== "number") return null;
    // Stale after 2h of idle browsing.
    if (Date.now() - o.at > 2 * 60 * 60 * 1000) return null;
    return {
      lastUser: o.lastUser,
      lastAgentPreview: String(o.lastAgentPreview ?? ""),
      // Always re-derive so label improvements apply without a new turn.
      topicLabel: topicLabelFromUserQuestion(o.lastUser),
      nextAskHints: Array.isArray(o.nextAskHints)
        ? o.nextAskHints.filter((x) => typeof x === "string" && x.trim())
        : [],
      at: o.at,
    };
  } catch {
    return null;
  }
}

export function writeConversationFocus(input: {
  lastUser: string;
  lastAgent: string;
  nextAskHints?: readonly string[];
}): ConversationFocus | null {
  const lastUser = input.lastUser.replace(/\s+/g, " ").trim();
  if (!lastUser || lastUser.length < 3) return null;
  // Skip host-only / mute lines.
  if (/^(stop talking|mute|got it\.|pick (your )?next)/i.test(lastUser)) return null;
  // Skip pure yes/no / nav ack — keeps topic from last real question (e.g. Traffic / Demo).
  if (
    /^(yes|yeah|yep|yup|no|nope|ok|okay|sure|please|thanks|thank you|alright|all right)([.!]*)?$/i.test(
      lastUser,
    )
  ) {
    return null;
  }

  const agent = input.lastAgent.replace(/\s+/g, " ").trim();
  const lastAgentPreview = agent.slice(0, 280);
  // Chips only — never invent tenant product/page labels from free text.
  const nextAskHints = (input.nextAskHints ?? [])
    .map((q) => q.replace(/\s+/g, " ").trim())
    .filter(Boolean)
    .filter((q, i, a) => a.findIndex((x) => x.toLowerCase() === q.toLowerCase()) === i)
    .slice(0, 8);
  const focus: ConversationFocus = {
    lastUser,
    lastAgentPreview,
    topicLabel: topicLabelFromUserQuestion(lastUser),
    nextAskHints,
    at: Date.now(),
  };
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(focus));
  } catch {
    /* private mode */
  }
  // Let snippet re-apply hero/rail text continuity after a real turn.
  try {
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("bhai-conversation-focus", { detail: { at: focus.at } }),
      );
    }
  } catch {
    /* ignore */
  }
  return focus;
}

export function clearConversationFocus(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Score a candidate question against conversation focus (higher = better).
 * Pure presentation rank — does not invent content.
 */
export function scoreQuestionAgainstFocus(
  question: string,
  focus: ConversationFocus | null,
): number {
  if (!focus) return 0;
  const qTok = tokenize(question);
  if (qTok.size === 0) return 0;
  const focusTok = tokenize(`${focus.lastUser} ${focus.lastAgentPreview}`);
  let hit = 0;
  for (const t of qTok) {
    if (focusTok.has(t)) hit += 1;
  }
  let score = hit / Math.max(qTok.size, 1);

  // Strong boost if SB already suggested this ask as a next chip.
  const qn = normalizeQuestion(question);
  for (const hint of focus.nextAskHints) {
    const hn = normalizeQuestion(hint);
    if (!hn) continue;
    if (qn === hn || qn.includes(hn) || hn.includes(qn)) {
      score += 1.5;
      break;
    }
  }
  return score;
}

function isAskedInSession(q: string, asked: ReadonlySet<string>): boolean {
  if (asked.size === 0) return false;
  const n = normalizeQuestion(q);
  if (n && asked.has(n)) return true;
  const c = canonicalQuestionKey(q);
  return Boolean(c && asked.has(c));
}

/**
 * Reorder pills within a fixed inventory (section fidelity first).
 * Never adds questions that weren't in the list.
 *
 * Sort order:
 *  1. Unasked first (session asked demoted — belt + suspenders on top of
 *     caller filterUnaskedPageQuestions)
 *  2. Higher conversation-focus score
 *  3. Original index (stable)
 */
export function rankQuestionsByFocus(
  questions: readonly string[],
  focus: ConversationFocus | null,
): string[] {
  if (questions.length <= 1) return [...questions];
  let asked: ReadonlySet<string> = new Set();
  try {
    asked = getAskedPageQuestions();
  } catch {
    /* no sessionStorage in some test envs */
  }
  return [...questions]
    .map((q, i) => ({
      q,
      asked: isAskedInSession(q, asked) ? 1 : 0,
      s: focus ? scoreQuestionAgainstFocus(q, focus) : 0,
      i,
    }))
    .sort((a, b) => a.asked - b.asked || b.s - a.s || a.i - b.i)
    .map((x) => x.q);
}

/**
 * True when this page/section inventory can continue the thread
 * (at least one pill scores above a stricter floor).
 * SB nextAskHints still override the floor when present in inventory.
 */
export function focusMatchesInventory(
  questions: readonly string[],
  focus: ConversationFocus | null,
  floor = FOCUS_MATCH_FLOOR,
): boolean {
  if (!focus || questions.length === 0) return false;
  // SB next-ask hints in inventory always count as continuity (override floor).
  if (focus.nextAskHints.length > 0) {
    const inv = new Set(questions.map(normalizeQuestion));
    for (const h of focus.nextAskHints) {
      const hn = normalizeQuestion(h);
      if (!hn) continue;
      if (inv.has(hn)) return true;
      for (const iq of inv) {
        if (iq.includes(hn) || hn.includes(iq)) return true;
      }
    }
  }
  let best = 0;
  for (const q of questions) {
    best = Math.max(best, scoreQuestionAgainstFocus(q, focus));
  }
  return best >= floor;
}

/**
 * URL path → slug phrases for continuity matching.
 * Fleet-wide: "/auto-accident-claims" → "auto accident claims", "accident", …
 * No product dictionary — only structural path tokens.
 */
export function pathSlugPhrases(path: string | null | undefined): string[] {
  if (!path) return [];
  const slug = path.split(/[?#]/)[0] ?? "";
  const segs = slug
    .split(/[/_.-]+/)
    .map((s) => s.toLowerCase().trim())
    .filter((s) => s.length >= 2 && !/^\d+$/.test(s));
  if (segs.length === 0) return [];
  const out: string[] = [];
  // Full multi-segment phrase first (strongest signal).
  if (segs.length >= 2) {
    const joined = segs.join(" ");
    if (joined.length >= 4) out.push(joined);
  }
  // Individual segments (skip ultra-generic crumbs).
  const weak = new Set(["www", "en", "us", "page", "index", "home", "html"]);
  for (const s of segs) {
    if (s.length >= 4 && !weak.has(s) && !out.includes(s)) out.push(s);
  }
  return out;
}

function focusCorpus(focus: ConversationFocus): string {
  return [
    focus.lastUser,
    focus.lastAgentPreview,
    focus.topicLabel,
    ...focus.nextAskHints,
  ]
    .join(" ")
    .replace(/\s+/g, " ")
    .toLowerCase()
    .trim();
}

/** True when a page phrase appears in the focus corpus (substring, word-ish). */
function phraseInCorpus(phrase: string, corpus: string): boolean {
  const p = phrase.replace(/\s+/g, " ").trim().toLowerCase();
  if (!p || p.length < 4 || !corpus) return false;
  if (corpus.includes(p)) return true;
  // Loose: all non-stop tokens of phrase present in corpus.
  const toks = [...tokenize(p)];
  if (toks.length === 0) return false;
  if (toks.length === 1) {
    // Single token needs a clear word boundary hit (avoid "cat" in "certificate").
    const re = new RegExp(`\\b${toks[0]!.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "i");
    return re.test(corpus);
  }
  return toks.every((t) => corpus.includes(t));
}

/**
 * True when this page can continue the thread (SPA land continuity).
 *
 * Fleet-wide only — no product dictionary.
 *
 * Signals used (in order):
 *  1. Section/page inventory overlap with focus (strong)
 *  2. Multi-token path slug / page title already named in the thread
 *  3. Engine next-ask chips vs page title/path
 *
 * Hero monologue is intentionally NOT used: marketing copy almost always
 * repeats the site brand, which falsely matches every page after "What is X?".
 */
export function focusMatchesPageContext(
  focus: ConversationFocus | null,
  ctx: {
    pageTopic?: string | null;
    path?: string | null;
    /** Ignored for matching (kept for call-site compatibility). */
    monologue?: readonly string[];
    inventory?: readonly string[];
  },
  floor = FOCUS_MATCH_FLOOR,
): boolean {
  if (!focus) return false;
  void ctx.monologue;
  const inventory = ctx.inventory ?? [];
  if (inventory.length > 0 && focusMatchesInventory(inventory, focus, floor)) {
    return true;
  }

  // Path + page title only — not monologue (brand spam on every land).
  const pageParts = [ctx.pageTopic ?? "", ...pathSlugPhrases(ctx.path)]
    .map((s) => s.replace(/\s+/g, " ").trim())
    .filter((s) => s.length >= 3);
  if (pageParts.length === 0 && focus.nextAskHints.length === 0) return false;

  const corpus = focusCorpus(focus);
  if (!corpus) return false;

  // Prefer multi-token phrases (path "auto accident claims", title "Roof repair").
  // Single-token hits need 2+ focus tokens overall so "Brand" alone never matches all pages.
  for (const part of pageParts) {
    const toks = [...tokenize(part)];
    if (toks.length >= 2 && phraseInCorpus(part, corpus)) return true;
    if (toks.length === 1 && phraseInCorpus(part, corpus)) {
      const focusToks = tokenize(corpus);
      // Require another substantive focus token beyond this one brand/page word.
      if (focusToks.size >= 2 && [...focusToks].some((t) => t !== toks[0])) {
        // Still too weak alone — only accept if path/title phrase is multi-seg path.
        // Single "pricing" path: accept when focus also talks about pricing terms.
        if (scoreQuestionAgainstFocus(part, focus) >= Math.max(floor, 0.5)) {
          return true;
        }
      }
    }
  }

  // Multi-token page bag score (title + path joined).
  const bag = pageParts.join(" ").trim();
  if (bag) {
    const bagToks = tokenize(bag);
    if (bagToks.size >= 2) {
      const score = scoreQuestionAgainstFocus(bag, focus);
      if (score >= floor) return true;
    }
  }

  // Engine next-ask chips vs page title/path (not monologue).
  const pageNorm = normalizeQuestion(bag);
  for (const h of focus.nextAskHints) {
    const hn = normalizeQuestion(h);
    if (!hn || hn.length < 4) continue;
    if (pageNorm && (pageNorm.includes(hn) || hn.includes(pageNorm))) return true;
    if (phraseInCorpus(h, bag.toLowerCase())) return true;
    for (const part of pageParts) {
      if (phraseInCorpus(part, hn) || phraseInCorpus(hn, part.toLowerCase())) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Topic labels weak for spoken "Related to …" (fleet-wide heuristics).
 * Single brand stubs and first-person fragments ("I upload…") are not spoken.
 * Ranking pills can still use a weaker label server-side via lastUser text.
 */
export function isSpeakableContinuityTopic(topic: string | null | undefined): boolean {
  const t = (topic ?? "").replace(/\s+/g, " ").trim();
  if (!t || t.length < 4) return false;
  // Nav yes/no and filler — never "You asked about yes"
  if (
    /^(yes|yeah|yep|yup|no|nope|ok|okay|sure|please|thanks|thank you|got it|alright|all right)$/i.test(
      t,
    )
  ) {
    return false;
  }
  // "Yes and show me…" / "Yes, show me the traffic…" — not a real topic
  if (/^(yes|yeah|yep|sure|ok|okay)\b/i.test(t)) return false;
  if (/^(that|this|it|them|here|there)$/i.test(t)) return false;
  if (isAwkwardTopicLabel(t)) return false;
  const toks = [...tokenize(t)];
  // Need at least two content tokens for a natural multi-word summary.
  if (toks.length >= 2) return true;
  return false;
}

/**
 * Tokens too common to prove THIS page continues the thread (fleet-wide).
 * "Related to how BlueHatAI usage wallets works" must not fire on every
 * page just because every pill says "BlueHatAI".
 */
const WEAK_TOPIC_MATCH_TOKENS = new Set([
  "bluehatai",
  "bluehat",
  "website",
  "websites",
  "works",
  "work",
  "help",
  "helps",
  "show",
  "shows",
  "page",
  "site",
  "product",
  "service",
  "company",
]);

/**
 * True when inventory actually continues the topic (strong content overlap),
 * not merely the site brand. Used before speaking "Related to {topic}".
 */
export function topicMatchesInventory(
  topic: string | null | undefined,
  inventory: readonly string[],
): boolean {
  const strong = [...tokenize(topic ?? "")].filter(
    (t) => t.length >= 4 && !WEAK_TOPIC_MATCH_TOKENS.has(t),
  );
  if (strong.length === 0 || inventory.length === 0) return false;
  const need = Math.min(2, strong.length);
  for (const q of inventory) {
    const qTok = tokenize(q);
    let shared = 0;
    for (const t of strong) {
      if (qTok.has(t)) shared += 1;
    }
    if (shared >= need) return true;
  }
  return false;
}

/** Title-case ALL-CAPS headings so leads don't read "On ON YOUR WEBSITE". */
function titleishBarHeading(heading: string): string {
  const t = heading.replace(/\s+/g, " ").trim();
  if (!t) return "";
  if (t === t.toUpperCase() && t.length <= 48) {
    return t.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return t;
}

/** Layout chrome — never use as bar lead. */
function isJunkBarHeading(heading: string): boolean {
  const t = heading.replace(/\s+/g, " ").trim().toLowerCase();
  if (!t) return true;
  if (
    /^(top of (the )?page|bottom of (the )?page|header|footer|nav|hero|banner)$/i.test(
      t,
    )
  ) {
    return true;
  }
  // "on your website", "your website", bare "website", etc.
  if (/\b(your |my |this |the )?(web)?site\b/.test(t) && t.split(/\s+/).length <= 4) {
    return true;
  }
  if (/^(home|homepage|website)$/i.test(t)) return true;
  // Marketing taglines used as section keys
  if (/^answer every visitor$/i.test(t)) return true;
  return false;
}

/**
 * Bar lead: engagement default, or continuity when inventory matches focus.
 * Never "On ON YOUR WEBSITE" — junk headings dropped; no double "On".
 */
export function buildEngagementBarLead(opts: {
  currentHeading?: string | null;
  nextHeading?: string | null;
  focus?: ConversationFocus | null;
  /** All candidate questions currently shown (current+next section). */
  inventory?: readonly string[];
}): string {
  let here = titleishBarHeading(opts.currentHeading ?? "");
  let ahead = titleishBarHeading(opts.nextHeading ?? "");
  if (here && isJunkBarHeading(here)) here = "";
  if (ahead && isJunkBarHeading(ahead)) ahead = "";
  // Avoid "On On Your Website" if a heading already starts with On.
  const stripLeadingOn = (s: string) =>
    s.replace(/^on\s+/i, "").trim() || s;
  if (here) here = stripLeadingOn(here);
  if (ahead) ahead = stripLeadingOn(ahead);
  // Re-check junk after strip (e.g. "On Your Website" → "Your Website" still weak)
  if (here && isJunkBarHeading(here)) here = "";
  if (ahead && isJunkBarHeading(ahead)) ahead = "";

  const focus = opts.focus ?? null;
  const inventory = opts.inventory ?? [];

  if (focus && focusMatchesInventory(inventory, focus)) {
    const rawTopic = (focus.topicLabel || "").replace(/\s+/g, " ").trim();
    const topicOk =
      rawTopic &&
      isSpeakableContinuityTopic(rawTopic) &&
      rawTopic.length <= 48 &&
      !/^(yes|no|that|ok)$/i.test(rawTopic);
    if (topicOk) {
      if (here) {
        return `You asked about ${rawTopic} — continue in ${here}.`;
      }
      return `You asked about ${rawTopic} — pick a question or ask anything.`;
    }
    // Weak topic (yes/nav) — section orient only, no "You asked about yes"
    if (here && ahead) return `${here} · next: ${ahead}. Ask me anything.`;
    if (here) return `${here} — ask me anything.`;
    return "Pick a question or ask me anything.";
  }

  if (here && ahead) {
    return `${here} · next: ${ahead}. Ask me anything.`;
  }
  if (here) {
    return `${here} — ask me anything.`;
  }
  if (ahead) {
    return `Coming up: ${ahead}. Or ask me anything now.`;
  }
  return "Ask me anything about this page.";
}
